import { FileSpreadsheet } from 'lucide-react'
import { Link, Navigate, useLocation, useOutletContext, useParams } from 'react-router'

import type { Permisos } from '../api/capacidades'
import { useSesion } from '../api/sesion'
import type { Sitio } from '../api/tipos'
import { Aviso, Boton } from '../componentes/ui'
import { AccesoDelUsuario } from '../cuenta/AccesoDelUsuario'
import { PermisosDeInvitacion } from '../cuenta/PermisosDeInvitacion'
import { direccionNuevaDeRespuestas } from '../marco/secciones'
import { ConstructorDeFormulario } from '../formularios/Constructor'
import { ListaDeFormularios } from '../formularios/ListaDeFormularios'
import { Editor, type PestanaExtra } from '../motor/Editor'
import { useEsquema } from '../motor/esquema'
import { Listado } from '../motor/Listado'
import { BotonVistaPrevia } from '../motor/VerEnLaWeb'
import { VersionesEnIdiomas } from '../motor/VersionesEnIdiomas'
import { COLECCIONES_POR_IDIOMA } from '../api/idiomas'
import { AccionesDeEstructura, AvisoSinPlantillas } from '../estructura/Acciones'
import { ListaDeTerminos } from '../estructura/Terminos'
import { PlantillasConVinculos } from '../estructura/Vinculos'
import { AvisosDeCobro } from './Cobro'
import { FichaDeEnvio, ListaDeEnvios } from './Envios'
import { FichaDe404 } from './Errores404'
import { AvisoDeUltimaImportacion, Importar } from './Importar'
import { Medios } from './Medios'
import { FichaDePasarela } from './Pasarelas'
import { FichaDePedido, ListaDePedidos } from './Pedidos'
import { PestanaDeGrupo } from './PestanaDeGrupo'
import { PestanaSeo } from './PestanaSeo'
import { TituloConPortada, UsarComoPortada } from './Portada'
import { Unico } from './Unico'
import { AccionesDeUsuario, AvisoDeInvitacion } from './Usuarios'

/**
 * Qué colección hay detrás de cada tramo del menú, y con qué extras. Es la única lista
 * «a mano» del panel, y no dice nada de campos: solo qué pestañas propias lleva cada
 * módulo, a qué nivel vive (espacio o web) y quién puede crear o borrar.
 */
/** Lo que el marco le pasa a cada pantalla: el espacio, la web activa, el saldo y qué puede hacer quien mira (`api/capacidades.ts`). */
export type Contexto = { espacio: number; permisos: Permisos; saldo: null | number; sitio: null | Sitio }

type Definicion = {
  coleccion: string
  nivel?: 'espacio' | 'sitio'
  seo?: boolean
  /** Lleva `datos`: una pestaña por cada grupo de campos que le aplique (plan del CMS dinámico §5.3). */
  datos?: boolean
  puedeCrear?: boolean
  puedeBorrar?: boolean
  soloLectura?: boolean
  /** Un documento por web: se abre directamente. */
  unico?: boolean
  vacio?: string
  /** La línea bajo el título del listado: qué es esto y para qué sirve. */
  descripcion?: string
  ocultarCampos?: string[]
  /**
   * Sus plantillas de bloques con «Vincular a…» (plan del CMS dinámico §7): qué campo es qué
   * plantilla. Una ficha o un archivo son de un tipo; `termino`, la de los términos de una taxonomía.
   */
  plantillas?: Record<string, 'archivo' | 'ficha' | 'termino'>
  /** Es parte de la estructura de la web: su listado ofrece ver la web con lo que está en borrador. */
  estructura?: boolean
}

export const MODULOS: Record<string, Definicion> = {
  paginas: {
    coleccion: 'paginas',
    seo: true,
    datos: true,
    descripcion: 'Las páginas de tu web, montadas con bloques. Publica cuando estén listas.',
    vacio: 'Crea la primera: será tu portada si le pones la dirección «inicio».',
  },
  entradas: { coleccion: 'entradas', seo: true, datos: true, descripcion: 'Administra y publica el contenido de tu blog.', vacio: 'Crea tu primera publicación y empieza a generar contenido para tu sitio.' },
  categorias: { coleccion: 'categorias', descripcion: 'Las categorías con las que se organizan las entradas y los productos. Una puede colgar de otra.' },
  etiquetas: { coleccion: 'etiquetas', descripcion: 'Etiquetas libres para cruzar entradas entre categorías.' },
  menus: { coleccion: 'navegacion', descripcion: 'Qué enlaces salen en la cabecera, el pie y el resto de menús de tu web.', vacio: 'Crea el de la cabecera y el del pie.' },
  medios: { coleccion: 'medios', descripcion: 'Las imágenes de esta web.' },
  productos: { coleccion: 'productos', seo: true, datos: true, descripcion: 'Tu catálogo: precios, existencias, opciones y variantes.' },
  // Estructura → Grupos de campos (plan del CMS dinámico §5): los campos a medida de la web y dónde aparecen.
  // Estructura → Tipos de contenido y Taxonomías (plan del CMS dinámico §2.1, §2.2 y §7): lo que en WordPress serían los
  // «custom post types» y sus taxonomías, con las plantillas de su ficha, su archivo y sus términos.
  'tipos-de-contenido': {
    coleccion: 'tipos-de-contenido',
    estructura: true,
    plantillas: { plantillaFicha: 'ficha', plantillaArchivo: 'archivo' },
    descripcion: 'Los tipos de contenido de tu web: «Propiedades», «Cursos», «Proyectos»… Cada uno con su dirección, sus campos, sus taxonomías y las plantillas de su ficha y su listado. Publícalo para que salga en la web y en el menú.',
    vacio: 'Crea tu primer tipo: por ejemplo, «Propiedad», con su ficha en «/propiedades/…».',
  },
  taxonomias: {
    coleccion: 'taxonomias',
    estructura: true,
    plantillas: { plantillaTermino: 'termino' },
    descripcion: 'Cómo se clasifican tus contenidos: «Distritos», «Operación», «Nivel»… Cada taxonomía tiene sus términos, y puede tener una página por término.',
    vacio: 'Crea tu primera taxonomía: por ejemplo, «Distrito», y dentro sus términos.',
  },
  'grupos-de-campos': {
    coleccion: 'grupos-de-campos',
    estructura: true,
    descripcion: 'Campos a medida para tus páginas, entradas, productos y servicios: cada grupo es una pestaña del editor, y sus reglas dicen en qué documentos aparece. Publícalo para que se vea en el editor.',
    vacio: 'Crea tu primer grupo: por ejemplo, «Datos técnicos» para los productos de una categoría.',
  },
  servicios: {
    coleccion: 'servicios',
    seo: true,
    datos: true,
    descripcion: 'Lo que prestas: cada servicio con su ficha, su foto, su precio (fijo, «desde» o a consultar) y su botón de «Consultar». Salen en tu web en «/servicios».',
    vacio: 'Crea tu primer servicio. Si lo dejas en borrador, no sale en la web hasta que lo publiques.',
  },
  'categorias-de-servicios': {
    coleccion: 'categorias-de-servicios',
    descripcion: 'Las categorías de tus servicios, separadas de las de la tienda y del blog. Una puede colgar de otra; solo salen en la web las que tienen algún servicio publicado.',
    vacio: 'Crea una categoría para ordenar tus servicios: «Reparaciones», y dentro «Portátiles» y «Móviles».',
  },
  // La agenda (§33). Las reservas las crea el motor (por la web o por «Nueva reserva»), no el formulario genérico.
  reservas: { coleccion: 'reservas', puedeCrear: false, descripcion: 'Las reservas y citas de tu agenda: de quién, para qué y cuándo.', vacio: 'Cuando alguien reserve desde tu web, o apuntes una cita que llegó por teléfono, aparecerá aquí.' },
  agendas: { coleccion: 'agendas', puedeCrear: false, puedeBorrar: false, descripcion: 'El horario general, cada cuánto empieza un hueco, la antelación, si se confirma solo y hasta cuándo se puede cancelar.' },
  recursos: { coleccion: 'recursos', descripcion: 'Lo que se reserva: profesionales, espacios o unidades, cada uno con su capacidad y, si quieres, su propio horario.', vacio: 'Activa la agenda con la plantilla de tu negocio y se crean solos.' },
  bloqueos: { coleccion: 'bloqueos', descripcion: 'Vacaciones, feriados o un rato cerrado: de un recurso, o de toda la agenda si lo dejas vacío.', vacio: 'Ningún cierre apuntado: se puede reservar en todo el horario.' },
  'clientes-de-agenda': { coleccion: 'clientes-de-agenda', descripcion: 'Quién ha reservado, con cuántas reservas y cuántas veces no vino.', vacio: 'Cada persona que reserva aparece aquí, una sola vez aunque vuelva.' },
  pedidos: { coleccion: 'pedidos', puedeCrear: false, puedeBorrar: false, descripcion: 'Lo que han comprado, cómo lo pagaron y en qué estado está cada pedido.', vacio: 'Cuando alguien compre, su pedido aparecerá aquí.' },
  pagos: {
    coleccion: 'credenciales-de-pago',
    descripcion: 'Con qué pasarela cobra tu tienda. Una activa a la vez; las claves se guardan cifradas. Al abrir una, verás cómo configurarla paso a paso.',
    vacio: 'Sin pasarela, la tienda no cobra en línea: al terminar, el comprador lee el mensaje de Ajustes → Tienda con la forma de pago. Escríbelo ahí, o añade aquí una pasarela.',
  },
  formularios: { coleccion: 'formularios', descripcion: 'Los formularios de tu web: qué se pregunta, en qué orden y a dónde va lo que llega.' },
  // Lo que llega por los formularios: para el cliente son «Respuestas» (la colección sigue siendo `envios`).
  // `…/envios` y `…/envios/<id>`, las direcciones de antes, siguen valiendo: van en correos ya enviados (abajo).
  respuestas: { coleccion: 'envios', puedeCrear: false, descripcion: 'Lo que la gente ha respondido en los formularios de tu web.', vacio: 'Cuando alguien rellene un formulario de tu web, su respuesta aparecerá aquí, y te llegará un aviso por correo.' },
  redirecciones: { coleccion: 'redirecciones', descripcion: 'Direcciones viejas que llevan a las nuevas, para no perder visitas ni posiciones.' },
  '404': { coleccion: 'errores-404', puedeCrear: false, descripcion: 'Direcciones que la gente pide y no existen. Conviértelas en redirecciones.', vacio: 'Ninguna dirección rota apuntada. Buena señal.' },
  versiones: { coleccion: 'versiones-de-tema', puedeCrear: false, puedeBorrar: false, soloLectura: true, descripcion: 'La historia del diseño de tu web: quién cambió qué y cuándo.' },
  solicitudes: { coleccion: 'solicitudes-de-publicacion', puedeCrear: false, puedeBorrar: false, soloLectura: true, descripcion: 'Los cambios de diseño que esperan la revisión del equipo.' },
  ajustes: { coleccion: 'ajustes', puedeCrear: false, puedeBorrar: false, unico: true, descripcion: 'La marca, el contacto, el SEO por defecto y la tienda de esta web.' },
  usuarios: { coleccion: 'usuarios', nivel: 'espacio', ocultarCampos: ['clientes'], descripcion: 'Quién entra en este espacio y qué puede hacer.' },
  // «Quién» y «Qué pasó» los escribe el núcleo en llano; la relación con el usuario no se enseña (el del equipo no es legible para el cliente).
  auditoria: {
    coleccion: 'auditoria',
    nivel: 'espacio',
    puedeCrear: false,
    puedeBorrar: false,
    soloLectura: true,
    ocultarCampos: ['usuario', 'usuarioReal'],
    descripcion: 'Todo lo que ha cambiado algo que importa, con quién y cuándo.',
  },
  // Los dominios se conectan en su pantalla («Dominio»), que dice qué poner en el DNS y comprueba si funciona.
  sitios: { coleccion: 'sitios', nivel: 'espacio', puedeBorrar: false, ocultarCampos: ['versionPublicada', 'versionAnteriorPublicada', 'dominios'], descripcion: 'Las webs de este espacio de trabajo.' },
}

export const ModuloGenerico = () => {
  const { modulo = '', id } = useParams()
  const { espacio, sitio, permisos } = useOutletContext<Contexto>()
  const { usuario } = useSesion()
  const { pathname, search } = useLocation()
  // Las respuestas de los formularios antes se llamaban «Envíos»: sus direcciones, que van en correos ya enviados, llevan a las nuevas.
  const nueva = modulo === 'envios' ? direccionNuevaDeRespuestas(pathname, search) : null
  const definicion = MODULOS[modulo]
  // Los grupos de campos que aplican al documento (o a uno nuevo de esta web), para pintar una pestaña por grupo.
  // Lo dice el núcleo con el esquema; el panel no evalúa ninguna regla. Antes de cualquier salida: es un hook.
  const idNumerico = id !== undefined && /^\d+$/.test(id) ? Number(id) : null
  const { data: esquemaConGrupos } = useEsquema(definicion?.coleccion ?? '', {
    id: definicion?.datos && id !== undefined ? idNumerico : null,
    sitio: definicion?.datos && id === 'nuevo' ? (sitio?.id ?? null) : null,
    enabled: Boolean(definicion?.datos) && id !== undefined && (idNumerico !== null || id === 'nuevo'),
  })
  if (nueva) return <Navigate replace to={nueva} />
  if (!definicion) return <Aviso tono="error">Esa sección no existe.</Aviso>

  const deEspacio = definicion.nivel === 'espacio'
  const base = deEspacio ? `/e/${espacio}/${modulo}` : sitio ? `/e/${espacio}/s/${sitio.id}/${modulo}` : `/e/${espacio}/${modulo}`
  if (!deEspacio && !sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  const idSitio = deEspacio ? null : (sitio?.id ?? null)

  // Los módulos con pantalla propia.
  if (modulo === 'medios') return <Medios />
  if (definicion.unico && idSitio)
    return (
      <Unico
        base={base}
        // En Ajustes, si la tienda no dice cómo pagar: es donde se arregla.
        cabecera={
          definicion.coleccion === 'ajustes'
            ? (valores) => {
                // Con lo que hay escrito ahora en el formulario: el aviso se va en cuanto se escribe el mensaje o se pasa a catálogo.
                const tienda = (valores.tienda ?? {}) as { mensajeDePedido?: unknown; modo?: unknown }
                return (
                  <div className="flex flex-col gap-3">
                    <AvisosDeCobro catalogo={tienda.modo === 'catalogo'} espacio={espacio} mensajeEscrito={typeof tienda.mensajeDePedido === 'string' && tienda.mensajeDePedido.trim() !== ''} que="ajustes" sitio={idSitio} />
                    {/* Las tarifas de envío ya no están aquí (CLAUDE.md §23): tienen su pantalla. */}
                    {(sitio?.modulos ?? []).includes('productos') && (
                      <Aviso tono="info">
                        Las zonas de envío, el recojo en tienda y Google Maps están en{' '}
                        <Link className="font-medium text-violeta underline-offset-2 hover:underline" to={`/e/${espacio}/s/${idSitio}/entregas`}>
                          Envíos y entregas
                        </Link>
                        .
                      </Aviso>
                    )}
                  </div>
                )
              }
            : undefined
        }
        coleccion={definicion.coleccion}
        espacio={espacio}
        sitio={idSitio}
      />
    )
  if (modulo === 'respuestas' && idSitio)
    return id === undefined ? (
      <ListaDeEnvios base={base} descripcion={definicion.descripcion} espacio={espacio} sitio={idSitio} vacio={definicion.vacio} />
    ) : (
      <FichaDeEnvio base={base} espacio={espacio} permisos={permisos} sitio={idSitio} />
    )
  if (modulo === 'pedidos' && idSitio)
    return id === undefined ? (
      <ListaDePedidos base={base} descripcion={definicion.descripcion} espacio={espacio} sitio={idSitio} vacio={definicion.vacio} />
    ) : (
      <FichaDePedido base={base} espacio={espacio} permisos={permisos} sitio={idSitio} />
    )
  if (modulo === 'pagos' && idSitio && id !== undefined) return <FichaDePasarela base={base} espacio={espacio} sitio={idSitio} />
  // Los formularios tienen su constructor (lienzo, paleta y vista previa) y su listado con interruptor (formularios/).
  if (modulo === 'formularios' && idSitio)
    return id === undefined ? <ListaDeFormularios base={base} descripcion={definicion.descripcion} espacio={espacio} sitio={idSitio} /> : <ConstructorDeFormulario base={base} espacio={espacio} sitio={idSitio} />
  if (modulo === '404' && idSitio && id !== undefined) return <FichaDe404 base={base} espacio={espacio} permisos={permisos} sitio={idSitio} />
  // Productos → Importar (docs/alta-de-web-con-plantillas.md §7.3): la misma pantalla que usa el asistente, para después del alta.
  if (modulo === 'productos' && idSitio && id === 'importar') return <Importar base={base} espacio={espacio} sitio={idSitio} />

  // Las redirecciones las escribe quien lleva el SEO de toda la web (`seo.sitio`, §5): quien
  // edita el SEO de los documentos las mira. Un 404 sí lo convierte en redirección, desde su ficha.
  const soloMira = definicion.coleccion === 'redirecciones' && !permisos.tiene('seo.sitio')
  // El catálogo de la tienda es contenido, y quien solo edita lo suyo no lo administra (ni importa cien productos).
  const administraElCatalogo = permisos.tiene('contenido.editarAjeno')

  if (id !== undefined) {
    const pestanas: PestanaExtra[] = []
    if (definicion.seo) pestanas.push({ clave: 'seo', etiqueta: 'SEO', campos: ['seo'], contenido: ({ coleccion, id: idDoc }) => <PestanaSeo coleccion={coleccion} id={idDoc} /> })
    // Una pestaña por grupo de campos que aplique; los valores van en `datos`, que el motor no pinta por su cuenta.
    for (const grupo of definicion.datos ? (esquemaConGrupos?.grupos ?? []) : []) {
      pestanas.push({ clave: `grupo-${grupo.id}`, etiqueta: grupo.pestana, campos: ['datos'], contenido: ({ coleccion, id: idDoc }) => <PestanaDeGrupo coleccion={coleccion} grupo={grupo} id={idDoc} /> })
    }
    const esUsuario = definicion.coleccion === 'usuarios'
    const idDelDoc = id === 'nuevo' ? null : Number(id)
    const esTaxonomia = definicion.coleccion === 'taxonomias'
    // Los términos de una taxonomía, en su pestaña (y en su pantalla: estructura/Terminos.tsx).
    if (esTaxonomia && idDelDoc !== null && idSitio) {
      pestanas.push({
        clave: 'terminos',
        etiqueta: 'Términos',
        contenido: ({ valores }) => (
          <ListaDeTerminos
            compacto
            espacio={espacio}
            puedeCrear={permisos.tiene('terminos.editar')}
            sitio={idSitio}
            taxonomia={{ id: idDelDoc, nombre: String(valores.nombre ?? ''), nombrePlural: String(valores.nombrePlural ?? valores.nombre ?? 'Términos') }}
          />
        ),
      })
    }
    const esPagina = definicion.coleccion === 'paginas'
    const esWeb = definicion.coleccion === 'sitios' && id !== 'nuevo'
    // Nombrar propietario es del dueño de la cuenta: a quien no lo es no se le ofrece (el núcleo lo rechaza igual).
    const presetsQuePuedeDar = permisos.esDueno ? undefined : (['administrador', 'editor', 'autor', 'marketing'] as const)
    // Las versiones en otros idiomas (§37), encima de lo que ya llevara la cabecera. Un documento nuevo en la
    // pestaña del inglés nace en inglés (`?idioma=en`, lo pone el listado).
    const porIdioma = COLECCIONES_POR_IDIOMA.has(definicion.coleccion)
    const idiomaNuevo = new URLSearchParams(search).get('idioma')
    const cabeceraPropia = esUsuario && idDelDoc === null
            ? () => (
                <div className="flex flex-col gap-4">
                  <AvisoDeInvitacion />
                  {/* El preset y las casillas escriben en el formulario: van en el cuerpo de la invitación (plan del CMS dinámico §4). */}
                  <PermisosDeInvitacion presetsDisponibles={presetsQuePuedeDar ? [...presetsQuePuedeDar] : undefined} />
                </div>
              )
            : definicion.plantillas && !permisos.tiene('plantillas.editar')
              ? () => <AvisoSinPlantillas />
              : esWeb
              ? () => (
                  <Aviso tono="info">
                    La dirección de esta web y sus dominios propios se gestionan en{' '}
                    <Link className="font-medium text-violeta underline-offset-2 hover:underline" to={`/e/${espacio}/s/${id}/dominio`}>
                      Dominio
                    </Link>
                    , que dice qué poner en tu proveedor y si ya funciona.
                  </Aviso>
                )
              : undefined
    const editor = (
      <Editor
        // Un usuario nuevo se invita: le llega un enlace para poner su contraseña (paginas/Usuarios.tsx).
        // Una página que no es la portada puede pasar a serlo (lo hace quien publica contenido, §13).
        acciones={
          definicion.plantillas && idSitio
            ? ({ id: idDoc, valores, recargar, sucio }) => <AccionesDeEstructura coleccion={definicion.coleccion} espacio={espacio} id={idDoc} recargar={recargar} sitio={idSitio} sucio={sucio} valores={valores} />
            : esUsuario
            ? ({ id: doc }) => <AccionesDeUsuario espacio={espacio} id={doc} permisos={permisos} yo={usuario?.id ?? null} />
            : esPagina && idSitio && permisos.tiene('contenido.publicar')
              ? ({ id: idDoc, valores, recargar, sucio }) =>
                  idDoc !== null && valores.esPortada !== true ? <UsarComoPortada alTerminar={recargar} id={idDoc} sitio={idSitio} sucio={sucio} titulo={String(valores.titulo ?? '')} /> : null
              : undefined
        }
        base={base}
        cabecera={
          porIdioma && idSitio && idDelDoc !== null
            ? () => (
                <div className="flex flex-col gap-4">
                  <VersionesEnIdiomas base={base} coleccion={definicion.coleccion} id={idDelDoc} sitio={idSitio} />
                  {cabeceraPropia?.()}
                </div>
              )
            : cabeceraPropia
        }
        campoTitulo={definicion.coleccion === 'auditoria' ? 'resumen' : undefined}
        coleccion={definicion.coleccion}
        espacio={espacio}
        id={idDelDoc}
        // Un usuario nuevo nace en este espacio; la colección no lleva `cliente` ni `sitio`. Su `rol` es el
        // preset de partida y lo escribe `PermisosDeInvitacion` junto con `preset` y `capacidades`.
        inicialExtra={esUsuario ? { clientes: [{ cliente: espacio }], rol: 'editor', preset: 'editor' } : porIdioma && idDelDoc === null && (idiomaNuevo === 'en' || idiomaNuevo === 'pt') ? { idioma: idiomaNuevo } : undefined}
        // Al invitar, el rol se elige como «punto de partida» en el bloque de permisos: el campo del esquema sobraría.
        // `datos` lo pintan las pestañas de los grupos (o nada, si ninguno aplica): el JSON crudo no se enseña.
        ocultarCampos={[...(definicion.ocultarCampos ?? []), ...(esUsuario && idDelDoc === null ? ['rol'] : []), ...(definicion.datos ? ['datos'] : [])]}
        pestanasExtra={pestanas}
        // Un tipo o una taxonomía se previsualizan con su plantilla en borrador; un grupo de campos, con toda la estructura pendiente (§15).
        previa={
          definicion.plantillas && idDelDoc !== null
            ? { alcance: 'tipo', documento: { coleccion: definicion.coleccion as 'taxonomias' | 'tipos-de-contenido', id: idDelDoc } }
            : definicion.coleccion === 'grupos-de-campos'
              ? { alcance: 'estructura' }
              : undefined
        }
        puedeBorrar={soloMira ? false : definicion.puedeBorrar}
        rutaDeCreacion={esUsuario ? '/panel/usuarios/invitar' : undefined}
        sinPertenencia={esUsuario}
        sinSitio={definicion.coleccion === 'sitios'}
        sitio={idSitio}
        soloLectura={definicion.soloLectura || soloMira}
        textoDeCrear={esUsuario ? 'Invitar' : undefined}
      />
    )
    // Las plantillas de un tipo o de una taxonomía: cada campo de sus bloques puede vincularse a un dato del documento.
    if (definicion.plantillas) {
      const plantillas = definicion.plantillas
      return (
        <PlantillasConVinculos
          origenDe={(ruta) => {
            const contexto = plantillas[ruta]
            if (!contexto || idDelDoc === null) return null
            return contexto === 'termino' ? { taxonomia: idDelDoc } : { tipo: idDelDoc, contexto }
          }}
          sinGuardar={idDelDoc === null}
        >
          {editor}
        </PlantillasConVinculos>
      )
    }
    // En la ficha de una persona, debajo: qué puede hacer en **esta** cuenta (su fila de `accesos`).
    if (esUsuario && idDelDoc !== null) {
      return (
        <>
          {editor}
          <AccesoDelUsuario espacio={espacio} permisos={permisos} usuario={idDelDoc} yo={usuario?.id ?? null} />
        </>
      )
    }
    return editor
  }

  // En Productos, la última importación va encima de la lista: es la manera de volver a su informe después de salir de la pantalla.
  return (
    <Listado
      accion={
        modulo === 'productos' && administraElCatalogo ? (
          <Link to={`${base}/importar`}>
            <Boton icono={<FileSpreadsheet />}>Importar</Boton>
          </Link>
        ) : definicion.estructura && idSitio ? (
          // Los tipos, las taxonomías y los grupos en borrador, todos juntos sobre la web (§15).
          <BotonVistaPrevia pequeno={false} que={{ alcance: 'estructura' }} sitio={idSitio} texto="Ver la web con estos cambios" />
        ) : undefined
      }
      aviso={
        modulo === 'productos' && idSitio && administraElCatalogo ? (
          <AvisoDeUltimaImportacion base={base} sitio={idSitio} />
        ) : modulo === 'pagos' && idSitio ? (
          <AvisosDeCobro espacio={espacio} sitio={idSitio} />
        ) : undefined
      }
      base={base}
      coleccion={definicion.coleccion}
      // En Páginas, la portada lleva su marca junto al título (la dice el núcleo: `esPortada`).
      columnasPropias={
        definicion.coleccion === 'paginas'
          ? { titulo: { clave: 'titulo', etiqueta: 'Título', celda: (doc) => <TituloConPortada esPortada={doc.esPortada === true} titulo={String(doc.titulo ?? '')} /> } }
          : definicion.coleccion === 'auditoria'
            ? // «Qué pasó» y «Quién» los calcula el núcleo al leer: no son columnas de la base y no se puede ordenar por ellas.
              {
                resumen: { clave: 'resumen', etiqueta: 'Qué pasó', celda: (doc) => String(doc.resumen ?? doc.accion ?? ''), ordenable: false },
                quien: { clave: 'quien', etiqueta: 'Quién', celda: (doc) => String(doc.quien ?? '—'), ordenable: false },
              }
            : undefined
      }
      espacio={espacio}
      porCliente={deEspacio}
      // Crear una web es del dueño de la cuenta (cuántas, lo dice el plan: MisSitios.tsx).
      puedeCrear={soloMira ? false : (definicion.puedeCrear ?? (definicion.coleccion === 'sitios' ? permisos.esDueno : true))}
      sinSitio={definicion.coleccion === 'usuarios'}
      sitio={idSitio}
      descripcion={definicion.descripcion}
      vacio={definicion.vacio}
      whereBase={definicion.coleccion === 'usuarios' ? { 'where[clientes.cliente][equals]': String(espacio) } : undefined}
    />
  )
}
